import { CompanyConfig } from 'types';

export let config: CompanyConfig;

// The server render happens at build time (`output: 'export'`), where nothing
// fetches: seed an empty config so nothing there reads `undefined`.
if (typeof window === 'undefined') {
  config = {} as CompanyConfig;
}

export const apiBaseUrl = process.env.NEXT_PUBLIC_API_ENDPOINT || '/api'

/**
 * `config` is a module binding that `loadConfig` reassigns, which React cannot
 * see. That is safe below `AppConfigProvider`, which withholds its children
 * until the fetch settles; anything mounted above it — `FeatureFlagProvider`,
 * the nav inside `BaseLayout` — reads it through `useAppConfig` instead and
 * re-renders when the config lands.
 *
 * The binding doubles as the store snapshot: `undefined` until the fetch
 * resolves, then one object that is never reassigned again, so it is
 * referentially stable for `useSyncExternalStore` without a wrapper object.
 */
const configSubscribers = new Set<() => void>();

export function subscribeToConfig(onLoaded: () => void): () => void {
  configSubscribers.add(onLoaded);

  return () => {
    configSubscribers.delete(onLoaded);
  };
}

export const getConfig = (): CompanyConfig | undefined => config;

/** Logged when a subscriber throws; the remaining subscribers are notified regardless. */
const SUBSCRIBER_NOTIFY_FAILURE = 'An app configuration subscriber failed to handle the loaded config';

/**
 * Tells every subscriber the config has landed, whatever any one of them does.
 *
 * Guarded per subscriber rather than once around the loop: `Set.forEach`
 * abandons the rest of its iteration as soon as a callback throws, so one bad
 * subscriber would strand every later one — leaving components above
 * `AppConfigProvider` rendering against a config they were never told about.
 * The throw would also reach `loadConfig`'s `catch` and be logged as a failed
 * fetch, naming a cause that is not what happened.
 */
function notifyConfigSubscribers() {
  configSubscribers.forEach(notify => {
    try {
      notify();
    } catch (notificationFailure) {
      console.error(SUBSCRIBER_NOTIFY_FAILURE, notificationFailure);
    }
  });
}

export async function loadConfig() {
  try {
    const response = await fetch(`${apiBaseUrl}/config`);

    // An error response still carries a body, and a JSON one would otherwise
    // be adopted as the config.
    if (!response.ok) {
      throw new Error(`/config responded ${response.status} ${response.statusText}`);
    }

    config = await response.json();
    notifyConfigSubscribers();
  } catch (e) {
    // A failed load leaves `config` undefined, the same as one still in
    // flight: there is no LaunchDarkly client id to connect with either way,
    // and every flag stays on its default. No third state is needed.
    console.error('Error loading app configuration', e);
  }
}
